import { Camera, PerspectiveCamera, OrthographicCamera, WebGLRenderer } from "three";


const observers = new WeakMap<Camera, ResizeObserver>();

/**
 * Sets up our standard resize handler for 
 */
export const setupResizeHandler = (canvas: HTMLCanvasElement,
                                   camera: Camera,
                                   renderer: WebGLRenderer,
                                   callback: () => void) =>
{
    let observer = observers.get(camera);
    if(observer == null) {
        const handler = makeResizeHandler(canvas, camera, renderer);
        observer = new ResizeObserver(() => {
            handler();
            callback();
        });
        observer.observe(canvas);
    }
}

/**
 * Figures out which resize procedure to use for the type of camera we have.
 */
const makeResizeHandler = (canvas: HTMLCanvasElement,
                           camera: Camera,
                           renderer: WebGLRenderer) =>
{
    if(camera instanceof PerspectiveCamera) {
        return makePerspectiveResizeHandler(canvas, camera, renderer);
    } else if(camera instanceof OrthographicCamera) {
        return makeOrthoResizeHandler(canvas, camera, renderer);
    } else {
        throw new Error('Unsupported camera type', { cause: camera });
    }
}

/**
 * Returns the actual resize handler for an orthographic camera.
 */
const makeOrthoResizeHandler = (canvas: HTMLCanvasElement,
                                camera: OrthographicCamera,
                                renderer: WebGLRenderer) => 
{

    const originalSpan = (camera.top - camera.bottom) / 2;
    return () => {
        const
            width = canvas.clientWidth,
            height = canvas.clientHeight,
            aspect = width/height;

        renderer.setSize(width, height, false);
        camera.left = -originalSpan * aspect;
        camera.right = originalSpan * aspect;
        camera.top = originalSpan;
        camera.bottom = -originalSpan;

        camera.updateProjectionMatrix();
    }
}

/**
 * Returns the actual resize handler for a perspective camera.
 */
const makePerspectiveResizeHandler = (canvas: HTMLCanvasElement,
                                      camera: PerspectiveCamera,
                                      renderer: WebGLRenderer) => 
{
    return () => {
        const
            width = canvas.clientWidth,
            height = canvas.clientHeight,
            aspect = width/height;

        // Sets the size without updating the CSS style with the size.
        // Effectively changes the pixel dimensions of the output to
        // match the screen size of the canvas.
        renderer.setSize(width, height, false);

        // Note that you still have to run these methods on the camera or
        // the output will be distorted:
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
    }
}
